import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Typography, Box, Button, TextField,
  IconButton, InputAdornment,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MapboxAddressInput from '../../components/MapboxAddressInput';
import authService from '../../services/auth.service';
import partnerService from '../../services/partner.service';
import { useAuth } from '../../context/AuthContext';

/* -- Palette : tons chauds organiques, contraste mat -- */
const C = {
  purple: '#552583',
  purpleDark: '#33154f',
  gold: '#FDB927',
  bg: '#E4F0F6',
  white: '#ffffff',
  text: '#1c1917',
  textSoft: '#44403c',
  muted: '#78716c',
  borderLight: 'rgba(28,25,23,0.06)',
  borderMed: 'rgba(28,25,23,0.1)',
};

/* -- Grain -- */
const GRAIN = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E")`;

const getSteps = (type) =>
  type === 'delivery'
    ? ['Service', 'Identité', 'Documents']
    : ['Service', 'Identité', 'Activité', 'Documents'];

const partnerTypes = [
  {
    value: 'restaurant', label: 'Restaurant', icon: <RestaurantIcon />,
    description: 'Carte en ligne, commandes et livraison gérée par Usafiri.',
    commission: '15%', commissionLabel: '15% de commission',
    features: ['Menu digital', 'Commandes en ligne', 'Livraison', 'Click & Collect'],
    available: true, primary: true,
  },
  {
    value: 'delivery', label: 'Livreur indépendant', icon: <DeliveryDiningIcon />,
    description: 'Livrez repas, courses et colis à votre rythme.',
    commission: '5%', commissionLabel: 'Commission 5%',
    features: ['Horaires flexibles', 'Paiement quotidien'],
    available: true, primary: true,
  },
  {
    value: 'car_rental', label: 'Location de véhicules', icon: <TimeToLeaveIcon />,
    description: 'Digitalisez votre parc automobile.',
    commission: '20%', commissionLabel: '20% de commission',
    features: ['Réservation en ligne', 'Gestion du parc', 'Paiement sécurisé'],
    available: true,
  },
  {
    value: 'store', label: 'Commerce', icon: <StorefrontIcon />,
    description: 'Vendez vos produits en ligne.',
    commission: '12%', commissionLabel: '12% de commission',
    features: ['Vitrine en ligne', 'Gestion des stocks', 'Livraison'],
    available: false, comingSoon: true,
  },
  {
    value: 'vtc_driver', label: 'Société VTC', icon: <DirectionsCarIcon />,
    description: 'Intégrez vos chauffeurs à la plateforme.',
    commission: '20%', commissionLabel: '20% de commission',
    features: ['Dispatch automatique', 'Suivi GPS', 'Facturation'],
    available: false, comingSoon: true,
  },
  {
    value: 'logistics', label: 'Logistique', icon: <LocalShippingIcon />,
    description: 'Transport de colis et marchandises.',
    commission: '18%', commissionLabel: '18% de commission',
    features: ['Suivi en temps réel', 'Gestion des colis'],
    available: false, comingSoon: true,
  },
];

const primaryTypes = partnerTypes.filter(t => t.primary);
const secondaryTypes = partnerTypes.filter(t => !t.primary);

/* -- Input minimaliste -- */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: 'rgba(255,255,255,0.55)', borderRadius: '6px',
    fontSize: '0.88rem',
    '& fieldset': { borderColor: C.borderMed, borderWidth: '0.5px' },
    '&:hover fieldset': { borderColor: 'rgba(85,37,131,0.3)' },
    '&.Mui-focused fieldset': { borderColor: C.purple, borderWidth: '1.5px' },
  },
  '& input, & textarea': { color: C.text },
  '& .MuiInputLabel-root': { color: C.muted, fontSize: '0.84rem' },
  '& .MuiInputLabel-root.Mui-focused': { color: C.purple },
};

const labelSx = { color: C.muted, mb: 0.8, display: 'block', fontWeight: 600, fontSize: '0.76rem', letterSpacing: '0.02em' };

const PartnerRegister = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [activeStep]);

  useEffect(() => {
    if (user) {
      setFormData(p => ({
        ...p,
        first_name: p.first_name || user.first_name || '',
        last_name: p.last_name || user.last_name || '',
        email: p.email || user.email || '',
        phone: p.phone || user.phone || '',
      }));
    }
  }, [user]);

  const [selectedType, setSelectedType] = useState('');
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '', last_name: user?.last_name || '',
    email: user?.email || '', phone: user?.phone || '',
    password: '', confirm_password: '',
    home_address: '', home_address_data: null,
    home_street_number: '', home_street: '', home_city: '', home_postal_code: '',
    business_name: '', business_address: '', business_city: '',
    business_postal_code: '', business_description: '',
    bank_account: '', bank_name: '', iban: '',
    id_card: null, business_license: null, kbis: null, driver_license: null,
    tax_id: '', vehicle_type: 'moto',
    vehicle_brand: '', vehicle_plate: '', vehicle_year: '', vehicle_color: '',
  });
  const [error, setError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [dragOver, setDragOver] = useState(null);

  const selectedTypeInfo = partnerTypes.find(t => t.value === selectedType);
  const steps = getSteps(selectedType);
  const isDelivery = selectedType === 'delivery';

  const passwordStrength = useMemo(() => {
    const pwd = formData.password;
    if (!pwd) return { score: 0, label: '', color: 'inherit' };
    let score = 0;
    if (pwd.length >= 6) score++;
    if (pwd.length >= 10) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    if (score <= 1) return { score: 20, label: 'Faible', color: '#dc2626' };
    if (score === 2) return { score: 40, label: 'Faible', color: '#ea580c' };
    if (score === 3) return { score: 60, label: 'Moyen', color: C.gold };
    if (score === 4) return { score: 80, label: 'Fort', color: C.purple };
    return { score: 100, label: 'Excellent', color: C.purpleDark };
  }, [formData.password]);

  const handleChange = (e) => { setFormData(p => ({ ...p, [e.target.name]: e.target.value })); if (error) setError(''); };
  const handleFileUpload = (e, field) => { const f = e.target.files?.[0]; if (f) setFormData(p => ({ ...p, [field]: f })); };
  const handleDrop = (e, field) => { e.preventDefault(); setDragOver(null); const f = e.dataTransfer.files?.[0]; if (f) setFormData(p => ({ ...p, [field]: f })); };

  const validateStep = () => {
    if (activeStep === 0 && !selectedType) { setError('Sélectionnez un type de service'); return false; }
    if (activeStep === 1) {
      if (!formData.first_name.trim() || !formData.last_name.trim()) { setError('Prénom et nom requis'); return false; }
      if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { setError('Email invalide'); return false; }
      if (!formData.phone.trim()) { setError('Téléphone requis'); return false; }
      if (!formData.home_street.trim() || !formData.home_city.trim()) {
        setError('Rue et ville de domicile requis');
        return false;
      }
      if (!isAuthenticated) {
        if (formData.password.length < 6) { setError('Minimum 6 caractères'); return false; }
        if (formData.password !== formData.confirm_password) { setError('Mots de passe différents'); return false; }
      }
    }
    if (activeStep === 2) {
      if (!isDelivery && !formData.iban.trim()) { setError("IBAN requis"); return false; }
      if (!isDelivery && !formData.business_name.trim()) { setError("Nom de l'activité requis"); return false; }
    }
    setError(''); return true;
  };

  const handleNext = () => { if (validateStep()) setActiveStep(p => p + 1); };
  const handleBack = () => { setError(''); setActiveStep(p => p - 1); };

  const fileToBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleSubmit = async () => {
    if (!formData.id_card) { setError("Pièce d'identité requise"); return; }
    if (!isDelivery && ['restaurant', 'store', 'vtc_driver'].includes(selectedType) && !formData.kbis) { setError("Extrait Kbis requis"); return; }
    setSubmitLoading(true); setError('');
    try {
      if (!isAuthenticated) {
        const registerRes = await authService.register({ email: formData.email.trim(), password: formData.password, phone: formData.phone.trim(), first_name: formData.first_name.trim(), last_name: formData.last_name.trim(), user_type: 'client' });
        if (!registerRes.success || !registerRes.token) { setError(registerRes.message || "Erreur lors de la création du compte"); setSubmitLoading(false); return; }
      }
      const documents = {};
      if (formData.id_card) documents.id_card = await fileToBase64(formData.id_card);
      if (formData.driver_license) documents.driver_license = await fileToBase64(formData.driver_license);
      if (formData.kbis) documents.kbis = await fileToBase64(formData.kbis);
      if (formData.business_license) documents.business_license = await fileToBase64(formData.business_license);
      await partnerService.apply({
        partner_type: selectedType,
        business_name: isDelivery ? `${formData.first_name} ${formData.last_name}` : formData.business_name.trim(),
        business_address: isDelivery ? null : `${formData.business_address}, ${formData.business_postal_code} ${formData.business_city}`.trim(),
        home_address: {
          address: `${formData.home_street_number} ${formData.home_street}`.trim(),
          city: formData.home_city,
          postal_code: formData.home_postal_code,
          fullAddress: `${formData.home_street_number} ${formData.home_street}, ${formData.home_postal_code} ${formData.home_city}`.trim(),
        },
        description: formData.business_description || (isDelivery ? `Véhicule: ${formData.vehicle_type} ${formData.vehicle_brand} ${formData.vehicle_color} - Plaque: ${formData.vehicle_plate}` : null),
        documents, iban: formData.iban || null, tax_id: formData.tax_id || null,
      });
      navigate('/partner/verification-pending');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Erreur lors de l'inscription");
    } finally { setSubmitLoading(false); }
  };

  const getBusinessLabel = () => ({
    restaurant: 'Nom du restaurant', store: 'Nom du commerce', car_rental: "Nom de l'agence",
    vtc_driver: 'Nom de la société VTC', logistics: "Nom de l'entreprise", delivery: 'Nom complet',
  }[selectedType] || "Nom de l'entreprise");

  const needsKbis = !isDelivery && ['restaurant', 'store', 'vtc_driver', 'car_rental', 'logistics'].includes(selectedType);
  const needsLicense = !isDelivery && ['restaurant', 'store'].includes(selectedType);

  /* -- File upload zone : epuree, feedback clair -- */
  const FileUploadZone = ({ fieldName, label, description, required }) => (
    <Box
      onDrop={e => handleDrop(e, fieldName)}
      onDragOver={e => { e.preventDefault(); setDragOver(fieldName); }}
      onDragLeave={() => setDragOver(null)}
      sx={{
        border: '0.5px solid',
        borderColor: formData[fieldName] ? C.purple : dragOver === fieldName ? 'rgba(85,37,131,0.35)' : C.borderMed,
        borderRadius: '8px', p: 2.5, textAlign: 'center', cursor: 'pointer',
        transition: 'border-color 0.15s, background 0.15s',
        bgcolor: formData[fieldName] ? 'rgba(85,37,131,0.03)' : 'rgba(255,255,255,0.4)',
        '&:hover': { borderColor: 'rgba(85,37,131,0.3)' },
      }}
    >
      <input type="file" id={`file-${fieldName}`} hidden accept="image/*,.pdf" onChange={e => handleFileUpload(e, fieldName)} />
      <label htmlFor={`file-${fieldName}`} style={{ cursor: 'pointer', display: 'block' }}>
        {formData[fieldName] ? (
          <Box>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, color: C.purple, mb: 0.5 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: C.purple, mb: 0.2 }}>{formData[fieldName].name}</Typography>
            <Typography sx={{ fontSize: '0.68rem', color: C.muted }}>{(formData[fieldName].size / 1024).toFixed(0)} Ko</Typography>
          </Box>
        ) : (
          <Box>
            <CloudUploadOutlinedIcon sx={{ fontSize: 22, color: C.muted, mb: 0.5, opacity: 0.5 }} />
            <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: C.text, mb: 0.2 }}>
              {label}{required ? ' *' : ''}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: C.muted }}>{description}</Typography>
          </Box>
        )}
      </label>
    </Box>
  );

  /* -- Navigation buttons -- */
  const NavButtons = ({ onNext, nextLabel, nextDisabled, loading: btnLoading }) => (
    <Box sx={{ display: 'flex', gap: 2, mt: 5 }}>
      {activeStep > 0 && (
        <Button onClick={handleBack} disabled={btnLoading}
          sx={{
            py: 1.2, px: 3, borderRadius: '6px', textTransform: 'none',
            fontWeight: 600, fontSize: '0.82rem',
            color: C.textSoft, border: `0.5px solid ${C.borderMed}`,
            '&:hover': { borderColor: C.textMuted, background: 'rgba(28,25,23,0.02)' },
          }}>
          Retour
        </Button>
      )}
      <Button fullWidth onClick={onNext} disabled={nextDisabled || btnLoading}
        sx={{
          py: 1.3, borderRadius: '6px', textTransform: 'none',
          fontWeight: 700, fontSize: '0.88rem',
          background: C.text, color: C.white,
          boxShadow: 'none', letterSpacing: '0.01em',
          transition: 'opacity 0.15s',
          '&:hover': { background: C.text, opacity: 0.85 },
          '&:disabled': { opacity: 0.2, background: C.text, color: C.white },
        }}>
        {btnLoading ? 'Chargement...' : nextLabel}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', background: C.bg, position: 'relative' }}>
      {/* Grain */}
      <Box sx={{ position: 'fixed', inset: 0, backgroundImage: GRAIN, backgroundRepeat: 'repeat', backgroundSize: '200px', pointerEvents: 'none', zIndex: 0 }} />

      <Box sx={{ maxWidth: 640, mx: 'auto', px: { xs: 3, sm: 4 }, position: 'relative', zIndex: 1, pb: 10 }}>

        {/* -- Header editorial -- */}
        <Box sx={{ pt: { xs: 5, md: 8 }, pb: { xs: 4, md: 5 } }}>
          <Typography sx={{
            color: C.muted, fontSize: '0.62rem', fontWeight: 700,
            letterSpacing: '0.14em', textTransform: 'uppercase', mb: 2,
          }}>
            Programme partenaire
          </Typography>
          <Typography sx={{
            fontWeight: 800, color: C.text,
            fontSize: { xs: '2rem', md: '2.8rem' },
            lineHeight: 1.02, letterSpacing: '-0.04em', mb: 1.5,
          }}>
            Rejoignez<br />
            <Box component="span" sx={{ color: C.purple }}>Usafiri</Box>
          </Typography>
          <Typography sx={{ color: C.muted, fontSize: '0.88rem', maxWidth: 380, lineHeight: 1.6 }}>
            Digitalisez votre activité avec la plateforme de référence à Mayotte.
          </Typography>
        </Box>

        {/* -- Step indicator minimal -- */}
        {selectedType && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 5 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: C.purple, fontVariantNumeric: 'tabular-nums' }}>
              {activeStep + 1}/{steps.length}
            </Typography>
            <Box sx={{ flex: 1, height: '1px', background: C.borderMed, position: 'relative' }}>
              <Box sx={{
                position: 'absolute', left: 0, top: 0, height: '2px',
                width: `${((activeStep + 1) / steps.length) * 100}%`,
                background: C.purple, borderRadius: 1,
                transition: 'width 0.4s cubic-bezier(0.4,0,0.2,1)',
                mt: '-0.5px',
              }} />
            </Box>
            <Typography sx={{ fontSize: '0.68rem', color: C.muted, fontWeight: 500 }}>
              {steps[activeStep]}
            </Typography>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Box sx={{
            mb: 4, py: 1.5, px: 2.5,
            borderLeft: '3px solid #ef4444',
            background: 'rgba(239,68,68,0.05)',
            fontSize: '0.82rem', fontWeight: 600, color: '#b91c1c',
          }}>
            {error}
          </Box>
        )}

        {/* ======== STEP 0: TYPE ======== */}
        {activeStep === 0 && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: C.text, mb: 0.5, letterSpacing: '-0.01em' }}>
              Quel est votre secteur ?
            </Typography>
           

            {/* Primary types -- large, asymetrique */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
              {primaryTypes.map((type) => {
                const active = selectedType === type.value;
                return (
                  <Box
                    key={type.value}
                    onClick={() => setSelectedType(type.value)}
                    sx={{
                      flex: 1, cursor: 'pointer',
                      p: 2.5, borderRadius: '10px',
                      position: 'relative',
                      background: active ? C.white : 'rgba(255,255,255,0.45)',
                      border: `${active ? '1.5px' : '0.5px'} solid ${active ? C.purple : C.borderMed}`,
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: active ? C.purple : 'rgba(85,37,131,0.25)' },
                    }}
                  >
                    {active && (
                      <Box sx={{
                        position: 'absolute', top: 12, right: 12,
                        width: 18, height: 18, borderRadius: '50%',
                        background: C.purple,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 14, color: C.white }} />
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Box sx={{
                        color: active ? C.purple : C.muted,
                        display: 'flex', transition: 'color 0.2s',
                      }}>
                        {React.cloneElement(type.icon, { sx: { fontSize: 20 } })}
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: C.text }}>{type.label}</Typography>
                    </Box>

                    <Typography sx={{ fontSize: '0.78rem', color: C.muted, lineHeight: 1.55, mb: 2 }}>
                      {type.description}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{
                        fontSize: '0.65rem', fontWeight: 700, color: C.purple,
                        letterSpacing: '0.04em',
                      }}>
                        {type.commissionLabel}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Secondary types -- ligne compacte */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: C.muted, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1.5 }}>
                Autres secteurs
              </Typography>
              {secondaryTypes.map((type, idx) => {
                const active = selectedType === type.value;
                return (
                  <Box
                    key={type.value}
                    onClick={() => { if (type.available) setSelectedType(type.value); }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      py: 1.3,
                      borderBottom: idx < secondaryTypes.length - 1 ? `0.5px solid ${C.borderLight}` : 'none',
                      cursor: type.available ? 'pointer' : 'default',
                      opacity: type.available ? 1 : 0.35,
                      transition: 'background 0.15s',
                      '&:hover': type.available ? { background: 'rgba(255,255,255,0.3)' } : {},
                    }}
                  >
                    <Box sx={{ color: active ? C.purple : C.muted, display: 'flex' }}>
                      {React.cloneElement(type.icon, { sx: { fontSize: 18 } })}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.84rem', color: type.available ? C.text : C.muted }}>
                        {type.label}
                      </Typography>
                    </Box>
                    {type.comingSoon && (
                      <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: C.gold, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                        Bientôt
                      </Typography>
                    )}
                    {active && <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: C.purple }} />}
                    {type.available && !active && !type.comingSoon && (
                      <Typography sx={{ fontSize: '0.7rem', color: C.muted, fontWeight: 500 }}>{type.commission}</Typography>
                    )}
                  </Box>
                );
              })}
            </Box>

         

            <NavButtons
              onNext={handleNext}
              nextLabel={selectedTypeInfo ? `Continuer` : 'Sélectionnez un secteur'}
              nextDisabled={!selectedType}
            />
          </Box>
        )}

        {/* ======== STEP 1: IDENTITY ======== */}
        {activeStep === 1 && (
          <Box>
            {/* Rappel type selectionne */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4, pb: 3, borderBottom: `0.5px solid ${C.borderLight}` }}>
              <Box sx={{ color: C.purple, display: 'flex' }}>
                {selectedTypeInfo && React.cloneElement(selectedTypeInfo.icon, { sx: { fontSize: 18 } })}
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: '0.82rem', color: C.text, flex: 1 }}>{selectedTypeInfo?.label}</Typography>
              <Typography sx={{ fontSize: '0.68rem', color: C.muted }}>{selectedTypeInfo?.commission}</Typography>
            </Box>

            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: C.text, mb: 0.5, letterSpacing: '-0.01em' }}>
              {isAuthenticated ? 'Confirmer vos informations' : 'Vos informations'}
            </Typography>
            <Typography sx={{ color: C.muted, fontSize: '0.82rem', mb: 4 }}>
              {isAuthenticated ? 'Votre compte sera converti en partenaire.' : 'Gérant ou représentant légal.'}
            </Typography>

            {isAuthenticated && (
              <Box sx={{
                mb: 4, py: 1.5, px: 2.5,
                borderLeft: `3px solid ${C.purple}`,
                background: 'rgba(85,37,131,0.03)',
                fontSize: '0.8rem', color: C.textSoft,
              }}>
                Connecté en tant que <strong>{user?.first_name} {user?.last_name}</strong>
              </Box>
            )}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <Box>
                <Typography sx={labelSx}>Prénom</Typography>
                <TextField fullWidth name="first_name" placeholder="Votre prénom" value={formData.first_name} onChange={handleChange} disabled={isAuthenticated} sx={inputSx} />
              </Box>
              <Box>
                <Typography sx={labelSx}>Nom</Typography>
                <TextField fullWidth name="last_name" placeholder="Votre nom" value={formData.last_name} onChange={handleChange} disabled={isAuthenticated} sx={inputSx} />
              </Box>
              <Box>
                <Typography sx={labelSx}>Email</Typography>
                <TextField fullWidth name="email" type="email" placeholder="votre@email.com" value={formData.email} onChange={handleChange} disabled={isAuthenticated} sx={inputSx} />
              </Box>
              <Box>
                <Typography sx={labelSx}>Téléphone</Typography>
                <TextField fullWidth name="phone" placeholder="+262 6XX XX XX XX" value={formData.phone} onChange={handleChange} disabled={isAuthenticated} sx={inputSx} />
              </Box>
              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                <Typography sx={labelSx}>Adresse de domicile</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1.5, mb: 1.5 }}>
                  <Box>
                    <Typography sx={{ ...labelSx, mb: 0.5 }}>N°</Typography>
                    <TextField fullWidth name="home_street_number" placeholder="12" value={formData.home_street_number} onChange={handleChange} sx={inputSx} />
                  </Box>
                  <Box>
                    <Typography sx={{ ...labelSx, mb: 0.5 }}>Rue *</Typography>
                    <TextField fullWidth name="home_street" placeholder="Rue des Flamboyants" value={formData.home_street} onChange={handleChange} required sx={inputSx} />
                  </Box>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 140px', gap: 1.5 }}>
                  <Box>
                    <Typography sx={{ ...labelSx, mb: 0.5 }}>Ville *</Typography>
                    <TextField fullWidth name="home_city" placeholder="Mamoudzou" value={formData.home_city} onChange={handleChange} required sx={inputSx} />
                  </Box>
                  <Box>
                    <Typography sx={{ ...labelSx, mb: 0.5 }}>Code postal</Typography>
                    <TextField fullWidth name="home_postal_code" placeholder="97600" value={formData.home_postal_code} onChange={handleChange} sx={inputSx} />
                  </Box>
                </Box>
              </Box>
              {!isAuthenticated && (
                <Box>
                  <Typography sx={labelSx}>Mot de passe</Typography>
                  <TextField fullWidth name="password" type={showPassword ? 'text' : 'password'} placeholder="Min. 6 caractères" value={formData.password} onChange={handleChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(p => !p)} size="small" sx={{ color: C.muted }}>{showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}</IconButton></InputAdornment>,
                    }}
                    sx={inputSx} />
                  {formData.password && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ flex: 1, height: 2, background: C.borderLight, borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ width: `${passwordStrength.score}%`, height: '100%', background: passwordStrength.color, transition: 'width 0.3s' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.62rem', color: passwordStrength.color, fontWeight: 700, flexShrink: 0 }}>{passwordStrength.label}</Typography>
                    </Box>
                  )}
                </Box>
              )}
              {!isAuthenticated && (
                <Box>
                  <Typography sx={labelSx}>Confirmer</Typography>
                  <TextField fullWidth name="confirm_password" type={showConfirmPassword ? 'text' : 'password'} placeholder="Retapez le mot de passe" value={formData.confirm_password} onChange={handleChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {formData.confirm_password && formData.password === formData.confirm_password && (
                            <CheckCircleOutlineIcon sx={{ color: C.purple, fontSize: 16, mr: 0.5 }} />
                          )}
                          <IconButton onClick={() => setShowConfirmPassword(p => !p)} size="small" sx={{ color: C.muted }}>{showConfirmPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}</IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx} />
                </Box>
              )}
            </Box>

            <NavButtons onNext={handleNext} nextLabel="Continuer" />
          </Box>
        )}

        {/* ======== STEP 2: BUSINESS (non-delivery) ======== */}
        {activeStep === 2 && !isDelivery && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: C.text, mb: 0.5, letterSpacing: '-0.01em' }}>Votre activité</Typography>
            <Typography sx={{ color: C.muted, fontSize: '0.82rem', mb: 5 }}>Établissement et coordonnées bancaires.</Typography>

            <Typography sx={{ color: C.muted, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 2.5 }}>
              Informations commerciales
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2, mb: 2 }}>
              <Box>
                <Typography sx={labelSx}>{getBusinessLabel()}</Typography>
                <TextField fullWidth name="business_name" placeholder={getBusinessLabel()} value={formData.business_name} onChange={handleChange} required sx={inputSx} />
              </Box>
              <Box>
                <Typography sx={labelSx}>Adresse</Typography>
                <MapboxAddressInput
                  placeholder="Adresse de votre activité"
                  value={formData.business_address}
                  onChange={(v) => setFormData(p => ({ ...p, business_address: v, business_city: p.business_address === v ? p.business_city : '', business_postal_code: p.business_address === v ? p.business_postal_code : '' }))}
                  onSelect={(result) => {
                    if (!result) { setFormData(p => ({ ...p, business_address: '', business_city: '', business_postal_code: '' })); return; }
                    setFormData(p => ({ ...p, business_address: result.fullAddress || result.place_name || result.address, business_city: result.city || '', business_postal_code: result.postal_code || '' }));
                  }}
                />
                <Typography sx={{ mt: 0.6, fontSize: '0.66rem', color: C.muted }}>
                  Sélectionnez l'adresse dans la liste pour valider.
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, mb: 2 }}>
              <Box>
                <Typography sx={labelSx}>Ville *</Typography>
                <TextField fullWidth name="business_city" placeholder="Mamoudzou" value={formData.business_city} onChange={handleChange} required sx={inputSx} />
              </Box>
              <Box>
                <Typography sx={labelSx}>Code postal</Typography>
                <TextField fullWidth name="business_postal_code" placeholder="97600" value={formData.business_postal_code} onChange={handleChange} sx={inputSx} />
              </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography sx={labelSx}>Description</Typography>
              <TextField fullWidth name="business_description" placeholder="Décrivez votre activité..." value={formData.business_description} onChange={handleChange} multiline rows={3} sx={inputSx} />
            </Box>

            <Box sx={{ height: '0.5px', background: C.borderMed, my: 5 }} />

            <Typography sx={{ color: C.muted, fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 2.5 }}>
              Coordonnées bancaires
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Typography sx={labelSx}>IBAN *</Typography>
              <TextField fullWidth name="iban" placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX" value={formData.iban} onChange={handleChange} required sx={inputSx} />
              <Typography sx={{ color: C.muted, mt: 0.6, fontSize: '0.68rem' }}>Revenus virés après déduction de la commission.</Typography>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <Box>
                <Typography sx={labelSx}>BIC / SWIFT</Typography>
                <TextField fullWidth name="bank_account" placeholder="BNPAFRPPXXX" value={formData.bank_account} onChange={handleChange} sx={inputSx} />
              </Box>
              <Box>
                <Typography sx={labelSx}>Banque</Typography>
                <TextField fullWidth name="bank_name" placeholder="Nom de la banque" value={formData.bank_name} onChange={handleChange} sx={inputSx} />
              </Box>
            </Box>

            <NavButtons onNext={handleNext} nextLabel="Continuer" />
          </Box>
        )}

        {/* ======== STEP: DOCUMENTS ======== */}
        {((isDelivery && activeStep === 2) || (!isDelivery && activeStep === 3)) && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: C.text, mb: 0.5, letterSpacing: '-0.01em' }}>Documents</Typography>
            <Typography sx={{ color: C.muted, fontSize: '0.82rem', mb: 4 }}>Dernière étape. Documents vérifiés sous 24-48h.</Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
              <FileUploadZone fieldName="id_card" label={isDelivery ? "Pièce d'identité" : "Pièce d'identité du gérant"} description="CNI, passeport ou titre de séjour" required />
              {isDelivery && (
                <FileUploadZone fieldName="driver_license" label="Permis de conduire" description="Si véhicule motorisé (optionnel)" />
              )}
              {isDelivery && (
                <Box>
                  <FileUploadZone fieldName="kbis" label="Extrait Kbis" description="Immatriculation (facultatif)" />
                  <Typography sx={{ mt: 0.6, fontSize: '0.68rem', color: C.muted }}>
                    Délai d'un mois après inscription.
                  </Typography>
                </Box>
              )}
              {needsKbis && <FileUploadZone fieldName="kbis" label="Extrait Kbis" description="Immatriculation" required />}
              {needsLicense && <FileUploadZone fieldName="business_license" label="Licence d'exploitation" description="Autorisation (optionnelle)" />}
            </Box>

            {!isDelivery && (
              <Box sx={{ mb: 4 }}>
                <Typography sx={labelSx}>Numéro d'identification fiscale (optionnel)</Typography>
                <TextField fullWidth name="tax_id" placeholder="Ex: 123456789" value={formData.tax_id} onChange={handleChange} sx={inputSx} />
              </Box>
            )}

            {/* Recapitulatif epure */}
            <Box sx={{ py: 3, borderTop: `0.5px solid ${C.borderMed}`, borderBottom: `0.5px solid ${C.borderMed}`, mb: 2 }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: C.muted, letterSpacing: '0.12em', textTransform: 'uppercase', mb: 2 }}>
                Récapitulatif
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
                {[
                  { label: 'Secteur', value: selectedTypeInfo?.label },
                  { label: 'Identité', value: `${formData.first_name} ${formData.last_name}` },
                  { label: isDelivery ? 'Véhicule' : 'Entreprise', value: isDelivery ? `${formData.vehicle_brand || '—'}` : formData.business_name || '—' },
                  { label: 'Commission', value: isDelivery ? '5%' : selectedTypeInfo?.commission, highlight: true },
                ].map((item, i) => (
                  <Box key={i}>
                    <Typography sx={{ fontSize: '0.62rem', color: C.muted, mb: 0.3 }}>{item.label}</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: item.highlight ? 800 : 700, color: item.highlight ? C.purple : C.text }}>
                      {item.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <NavButtons
              onNext={handleSubmit}
              nextLabel="Soumettre ma demande"
              loading={submitLoading}
            />
          </Box>
        )}

        {/* Login link */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography sx={{ color: C.muted, fontSize: '0.8rem' }}>
            Déjà partenaire ?{' '}
            <Link to="/login" style={{ color: C.purple, textDecoration: 'none', fontWeight: 700 }}>
              Se connecter
            </Link>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PartnerRegister;
